import { useTranslation } from "react-i18next";
import { COMPLIANCE_VERDICTS, type ComplianceVerdict } from "@njbsoft/api-client";
import { AccountSearchCombobox } from "@/components/accounts/account-search-combobox";

export interface ComplianceFilterValues {
  verdict?: ComplianceVerdict;
  /** The RESOLVED correspondence account the assemblies are addressed to. */
  account_id?: string;
}

interface ComplianceFiltersProps {
  filters: ComplianceFilterValues;
  onChange: (filters: ComplianceFilterValues) => void;
}

/**
 * Every verdict but `compliant`, in the enum home's precedence order. The
 * projection only returns non-compliant rows, so `compliant` is NEVER offered.
 * `terminal` and `pending_review` are EXPLICIT, non-default options — the
 * default result set EXCLUDES terminal rows per AWWA M14 §6.5 and devices whose
 * submitted test is still queued for review, so selecting either is the only
 * way to surface them. The route's deep-link allow-list reads this same list.
 */
export const OFFERED_VERDICTS = COMPLIANCE_VERDICTS.filter((v) => v !== "compliant");

const selectClass =
  "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white";

/**
 * Top filter bar for the compliance dashboard. Per `.claude/rules/frontend.md`
 * NO NAKED TABLES, the page owns the filter state + Reset; the `<DataTable>`
 * renders no filter inputs. The verdict select feeds the `verdict` axis; the
 * account Combobox is server-paged at limit=20 (`feedback_combobox_limits`) and
 * feeds the resolved-account axis `account_id`. There is NO `reason` filter and
 * NO `device_status` filter — the
 * backend does not accept those params (`reason` stays in the response as a
 * display-only "Why" label). `verdict: "compliant"` is deliberately NOT offered;
 * `verdict: "terminal"` and `verdict: "pending_review"` are offered as explicit
 * non-default surfaces for the historical, non-actionable terminal rows and the
 * tests still awaiting review that the default query excludes.
 */
export function ComplianceFilters({ filters, onChange }: ComplianceFiltersProps) {
  const { t } = useTranslation("compliance");

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs font-medium text-muted">
        {t("filters.verdictLabel")}
        <select
          value={filters.verdict ?? ""}
          onChange={(e) =>
            onChange({
              ...filters,
              verdict: (e.target.value as ComplianceVerdict) || undefined,
            })
          }
          className={selectClass}
        >
          <option value="">{t("filters.allVerdicts")}</option>
          {OFFERED_VERDICTS.map((v) => (
            <option key={v} value={v}>
              {t(`verdict.${v}`)}
            </option>
          ))}
        </select>
      </label>

      <div className="flex w-56 flex-col gap-1 text-xs font-medium text-muted">
        {t("filters.accountLabel")}
        <AccountSearchCombobox
          value={filters.account_id ?? null}
          onChange={(id) => onChange({ ...filters, account_id: id ?? undefined })}
          placeholder={t("filters.accountPlaceholder")}
          className="w-full"
        />
      </div>

      <button
        type="button"
        onClick={() => onChange({})}
        className="rounded-md border border-gray-300 px-3 py-2 text-sm text-muted hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
      >
        {t("filters.reset")}
      </button>
    </div>
  );
}
